async function jsonFetch(url: string, options?: object) {
  let response
  let payload
  try {
    response = await fetch(url, options)
    if (!(response.status >= 200 && response.status < 300)) {
      try {
        payload = await response.json()
      } catch {} // eslint-disable-line no-empty
      if (response.status === 404 && 'notFoundStrategy' in (options || {}) && (options as any).notFoundStrategy === 'ignore') {
        return undefined
      }
      const messages = [payload?.error?.message, payload?.message].filter((m) => m).join(' ')
      throw new Error(`Unable to fetch ${url}: ${response.status} ${response.statusText}.` + (messages ? ` ${messages}` : ''))
    }
    payload = await response.json()
    return payload
  } finally {
    if (import.meta.dev) {
      if (import.meta.server) {
        console.info(url)
      } else {
        console.groupCollapsed(url)
        console.info(response)
        console.info(payload)
        console.groupEnd()
      }
    }
  }
}

function constructUrl(url: string, query?: object | string, route?: ReturnType<typeof useRoute>) {
  const urlConstruct = new URL(url)

  if (query !== undefined) {
    if (typeof query === 'object') {
      Object.entries(query).forEach(([key, value]) => urlConstruct.searchParams.append(key, value))
    } else {
      Array.from(new URLSearchParams(query).entries()).forEach(([key, value]) => urlConstruct.searchParams.append(key, value))
    }
  }

  const preview = route?.query['x-craft-preview'] || route?.query['x-craft-live-preview'] || undefined
  if (typeof preview === 'string' && typeof route?.query.token === 'string') {
    urlConstruct.searchParams.append('preview', preview)
    urlConstruct.searchParams.append('token', route.query.token)
  }

  return urlConstruct.toString()
}

// Symfony bridge API endpoint
export const useBridgeFetch = () => {
  const config = useRuntimeConfig()
  const route = useRoute()
  return async (uri: string, options?: { method?: 'GET' | 'POST'; body?: string; query?: object }) => {
    return await jsonFetch(constructUrl(config.public['bridgeSiteUrl'] + uri, options?.query, route), options)
  }
}

// CMS API endpoint
export const useCmsFetch = () => {
  const config = useRuntimeConfig()
  const route = useRoute()
  return async (uri: string, options?: { query?: object; notFoundStrategy?: string }) => {
    return await jsonFetch(constructUrl(config.public['cmsSiteUrl'] + uri, options?.query, route), options)
  }
}

// GraphQL endpoint
export const useGraphqlFetch = () => {
  const config = useRuntimeConfig().public
  return async ({
    query,
    variables,
    token,
    headers,
    params,
  }: {
    query: string
    variables?: { [key: string]: undefined | null | number | string | string[] }
    token?: 'animal' | 'elasticsearch' | 'formie' | 'user'
    headers?: HeadersInit
    params?: URLSearchParams | object | any
  }) => {
    const tokenMap = {
      animal: config.animalGraphqlPublicToken,
      elasticsearch: config.elasticsearchGraphqlPublicToken,
      formie: config.formieGraphqlPublicToken,
      user: config.userGraphqlPublicToken,
    }

    const searchParams = params instanceof URLSearchParams ? params : params instanceof Object ? new URLSearchParams({ ...params }) : null

    let response
    let payload
    try {
      response = await fetch(`${config.cmsSiteUrl}/api${searchParams && searchParams.size > 0 ? `?${searchParams}` : ''}`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          ...(token && tokenMap[token] ? { Authorization: `Bearer ${tokenMap[token]}` } : {}),
          ...(headers || {}),
        },
        body: JSON.stringify({ query, variables }),
      })

      if (response.status !== 200) {
        throw new Error(`GraphQL error: invalid status ${response.status}`)
      }

      payload = await response.json()
      if (payload?.errors?.length) {
        // Do not prefix the error message, as it will be presented to the user
        throw new Error(payload?.errors?.[0]?.message || 'Unspecified GraphQL error')
      }

      return payload?.data
    } finally {
      if (import.meta.dev) {
        if (!import.meta.server || response?.status !== 200 || payload?.errors?.length) {
          console.groupCollapsed(`graphqlFetch - ${token}`)
          console.info(`token: ${token}`)
          console.info(query)
          console.info(variables)
          console.info(response)
          console.info(payload)
          console.groupEnd()
        } else {
          console.info(`graphqlFetch - ${token}`)
        }
      }
    }
  }
}
